import {canPersistAccount,registerAccountFlush} from './device-session';
import {useCallback,useEffect,useRef,useState} from 'react';
import type {Dispatch,SetStateAction} from 'react';
import type {User} from 'firebase/auth';
import {emptyJournal,restoreJournal,STORAGE_KEY} from '../domain';
import type {Journal} from '../domain';
import {applyJournalPatch,hasChanges,journalDiff,mergeGuestJournal} from '../../shared/journal';

const API=(import.meta.env.VITE_API_ORIGIN??'https://khojipark-api.raguaravinth.workers.dev').replace(/\/$/,'');
type Snapshot={journal:Journal;revision:number};
type Cache={journal:Journal;serverJournal:Journal;revision:number;guestImported:boolean};
type Status='local'|'connecting'|'syncing'|'synced'|'offline'|'error';
function initialCache(user:User|null):Cache{
  try{
    if(!user)return {journal:restoreJournal(localStorage.getItem(STORAGE_KEY)),serverJournal:emptyJournal(),revision:0,guestImported:false};
    const raw=JSON.parse(localStorage.getItem(`${STORAGE_KEY}:account:${user.uid}`)??'null');
    if(raw)return {journal:restoreJournal(JSON.stringify(raw.journal)),serverJournal:restoreJournal(JSON.stringify(raw.serverJournal)),revision:Number.isSafeInteger(raw.revision)?raw.revision:0,guestImported:raw.guestImported===true};
  }catch{/* Storage denial or invalid caches must not block reading. */}
  return {journal:emptyJournal(),serverJournal:emptyJournal(),revision:0,guestImported:false};
}
export function useJournalSync(user:User|null){
  const [cache]=useState(()=>initialCache(user));
  const [journal,render]=useState<Journal>(cache.journal),[status,setStatus]=useState<Status>(user?'connecting':'local'),[storageFailed,setStorageFailed]=useState(false);
  const local=useRef(journal),base=useRef(cache.serverJournal),revision=useRef(cache.revision),imported=useRef(cache.guestImported);
  const wake=useRef<()=>void>(()=>{});
  const persist=useCallback(()=>{
    try{
      if(user&&!canPersistAccount(user.uid))return;
      if(user)localStorage.setItem(`${STORAGE_KEY}:account:${user.uid}`,JSON.stringify({journal:local.current,serverJournal:base.current,revision:revision.current,guestImported:imported.current}));
      else localStorage.setItem(STORAGE_KEY,JSON.stringify(local.current));
      setStorageFailed(false);
    }catch{setStorageFailed(true);}
  },[user]);
  const setJournal:Dispatch<SetStateAction<Journal>>=useCallback(update=>{
    local.current=typeof update==='function'?update(local.current):update;
    render(local.current);persist();wake.current();
  },[persist]);
  useEffect(()=>{
    document.documentElement.dataset.theme=journal.theme;
  },[journal.theme]);
  useEffect(()=>{
    if(!user){persist();return;}
    let stopped=false,busy=false,hydrated=false,queued=false,timer:ReturnType<typeof setTimeout>|undefined;
    let guestSource:string|null=null,lastPull=0,dirtySince=0;
    const controller=new AbortController();
    async function request(path:string,body?:unknown):Promise<Snapshot>{
      const token=await user!.getIdToken();
      const response=await fetch(API+path,{method:body?'POST':'GET',headers:{Authorization:`Bearer ${token}`,...(body?{'Content-Type':'application/json'}:{})},...(body?{body:JSON.stringify(body)}:{}),keepalive:document.visibilityState==='hidden',signal:AbortSignal.any([controller.signal,AbortSignal.timeout(20000)])});
      if(!response.ok)throw Object.assign(Error('Sync unavailable'),{status:response.status});
      const data=await response.json();
      if(!Number.isSafeInteger(data.revision)||!data.journal)throw Error('Invalid journal response');
      return {revision:data.revision,journal:restoreJournal(JSON.stringify(data.journal))};
    }
    function publish(){if(stopped)return;render(local.current);persist();}
    function schedule(delay=5000){if(stopped)return;clearTimeout(timer);timer=setTimeout(()=>void synchronize(),delay);}
    async function pull(){
      const remote=await request('/api/me');if(stopped)return;
      const pending=journalDiff(base.current,local.current,1000);
      base.current=remote.journal;revision.current=remote.revision;
      local.current=applyJournalPatch(remote.journal,pending);
      if(!imported.current){
        try{
          const claim=JSON.parse(localStorage.getItem(`${STORAGE_KEY}:guest-claim`)??'null');
          if(!claim||claim.uid===user!.uid){guestSource=localStorage.getItem(STORAGE_KEY);if(guestSource)localStorage.setItem(`${STORAGE_KEY}:guest-claim`,JSON.stringify({uid:user!.uid,source:guestSource}));}
        }catch{/* No guest cache. */}
        if(guestSource){
          const guest=restoreJournal(guestSource);local.current=mergeGuestJournal(local.current,guest);
          if(remote.revision===0)local.current={...local.current,theme:guest.theme,textSize:guest.textSize};
        }
        imported.current=true;
      }
      if(!guestSource)try{const claim=JSON.parse(localStorage.getItem(`${STORAGE_KEY}:guest-claim`)??'null');if(claim?.uid===user!.uid)guestSource=claim.source;}catch{/* No import claim. */}
      hydrated=true;lastPull=Date.now();publish();
    }
    async function synchronize(){
      if(stopped)return;if(busy){queued=true;return;}
      if(!navigator.onLine){setStatus('offline');schedule(15000);return;}
      busy=true;queued=false;setStatus(hydrated?'syncing':'connecting');
      try{
        if(!hydrated)await pull();
        let turns=0;
        while(!stopped&&turns++<20){
          const patch=journalDiff(base.current,local.current);
          if(!hasChanges(patch))break;
          const sent=applyJournalPatch(base.current,patch);
          const remote=await request('/api/me/sync',{revision:revision.current,patch});if(stopped)return;
          const pending=journalDiff(sent,local.current,1000);
          base.current=remote.journal;revision.current=remote.revision;
          local.current=applyJournalPatch(remote.journal,pending);publish();
        }
        if(stopped)return;
        if(hasChanges(journalDiff(base.current,local.current))){schedule(2500);return;}
        // Claim only the exact guest snapshot that was imported. Never delete newer guest edits.
        if(guestSource)try{if(localStorage.getItem(STORAGE_KEY)===guestSource){localStorage.removeItem(STORAGE_KEY);localStorage.removeItem(`${STORAGE_KEY}:guest-claim`);}guestSource=null;}catch{/* Keep guest backup if storage is blocked. */}
        dirtySince=0;setStatus('synced');
      }catch(error){
        if(stopped)return;
        const code=(error as {status?:number}).status;
        if(code===409){hydrated=false;schedule(300);}
        else {setStatus(navigator.onLine?'error':'offline');schedule(code===429?65000:15000);}
      }finally{busy=false;if(queued&&!stopped)schedule();}
    }
    wake.current=()=>{
      const patch=journalDiff(base.current,local.current);
      if(!hasChanges(patch))return;
      dirtySince||=Date.now();
      const urgent=Boolean(patch.settings)||patch.stories.some(change=>Object.keys(change).some(key=>key!=='id'&&key!=='progress'));
      schedule(urgent?300:Math.max(0,Math.min(5000,dirtySince+15000-Date.now())));
    };
    const flush=async()=>{
      if(!hasChanges(journalDiff(base.current,local.current)))return true;
      if(!navigator.onLine)return false;
      clearTimeout(timer);
      const deadline=Date.now()+19000;
      while(busy&&!stopped&&Date.now()<deadline)await new Promise(resolve=>setTimeout(resolve,25));
      if(stopped||busy)return false;
      clearTimeout(timer);await synchronize();
      return !hasChanges(journalDiff(base.current,local.current));
    };
    const unregister=registerAccountFlush(user.uid,flush);
    const refresh=(force=false)=>{if(!force&&Date.now()-lastPull<30000)return;hydrated=false;schedule(100);};
    const online=()=>refresh(true);
    const visible=()=>{if(document.visibilityState==='visible')refresh();else void flush();};
    const leaving=()=>void flush();
    const poll=setInterval(()=>{if(document.visibilityState==='visible'&&!busy)refresh();},300000);
    addEventListener('online',online);addEventListener('pagehide',leaving);document.addEventListener('visibilitychange',visible);
    const storage=(event:StorageEvent)=>{if(event.key!==`${STORAGE_KEY}:account:${user.uid}`)return;try{if(JSON.parse(event.newValue??'null')?.revision>revision.current)refresh(true);}catch{/* Ignore invalid caches. */}};
    addEventListener('storage',storage);void synchronize();
    return ()=>{stopped=true;unregister();controller.abort();clearTimeout(timer);clearInterval(poll);wake.current=()=>{};removeEventListener('online',online);removeEventListener('pagehide',leaving);removeEventListener('storage',storage);document.removeEventListener('visibilitychange',visible);};
  },[user,persist]);
  return {journal,serverJournal:base.current,setJournal,status,storageFailed,retry:()=>wake.current()};
}
